export function symbolKey(ticker) {
  return String(ticker || '').trim().toUpperCase();
}

/** `marketAggregates` entry for a ticker, tolerant of key casing. */
export function bookForSymbol(marketAggregates, ticker) {
  if (!marketAggregates || !ticker) return null;
  const upper = symbolKey(ticker);
  if (upper && Object.prototype.hasOwnProperty.call(marketAggregates, upper)) return marketAggregates[upper];
  if (Object.prototype.hasOwnProperty.call(marketAggregates, ticker)) return marketAggregates[ticker];
  const match = Object.keys(marketAggregates).find((key) => symbolKey(key) === upper);
  return match ? marketAggregates[match] : null;
}

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Collapse depth rows to one per price, keeping every aggregate field
 * (order count, participants, biggest/smallest size, latest time).
 */
export function aggregateDepthSide(items) {
  if (!Array.isArray(items)) return [];
  const byPrice = new Map();
  for (const it of items) {
    if (!it || it.Price == null) continue;
    const priceKey = String(it.Price);
    const size = finiteNumber(it.TotalSize ?? it.Size) || 0;
    const prev = byPrice.get(priceKey) || {
      Price: priceKey,
      TotalSize: 0,
      TotalOrderCount: 0,
      NumParticipants: 0,
      BiggestSize: null,
      SmallestSize: null,
      EarliestTime: null,
      LatestTime: null,
    };
    prev.TotalSize += size;
    prev.TotalOrderCount += finiteNumber(it.TotalOrderCount) || 0;
    prev.NumParticipants += finiteNumber(it.NumParticipants) || 0;
    const biggest = finiteNumber(it.BiggestSize);
    if (biggest != null) prev.BiggestSize = prev.BiggestSize == null ? biggest : Math.max(prev.BiggestSize, biggest);
    const smallest = finiteNumber(it.SmallestSize);
    if (smallest != null) prev.SmallestSize = prev.SmallestSize == null ? smallest : Math.min(prev.SmallestSize, smallest);
    if (it.EarliestTime && (!prev.EarliestTime || it.EarliestTime < prev.EarliestTime)) prev.EarliestTime = it.EarliestTime;
    if (it.LatestTime && (!prev.LatestTime || it.LatestTime > prev.LatestTime)) prev.LatestTime = it.LatestTime;
    byPrice.set(priceKey, prev);
  }
  return Array.from(byPrice.values());
}

function bestPrice(levels, side) {
  let best = null;
  for (const level of Array.isArray(levels) ? levels : []) {
    const price = parseFloat(level?.Price);
    const size = parseFloat(level?.TotalSize ?? level?.Size);
    if (!Number.isFinite(price) || !(size > 0)) continue;
    if (best == null || (side === 'bid' ? price > best : price < best)) best = price;
  }
  return best;
}

/**
 * Drop levels that cross the other side (a missed deletion leaves a stale
 * level inside the market). `keep` names the side whose data is freshest.
 */
export function sanitizeBook(book, { keep = 'bid' } = {}) {
  const bids = Array.isArray(book?.bids) ? book.bids : [];
  const asks = Array.isArray(book?.asks) ? book.asks : [];
  const bestBid = bestPrice(bids, 'bid');
  const bestAsk = bestPrice(asks, 'ask');
  if (bestBid == null || bestAsk == null || bestBid < bestAsk) return { bids, asks };
  if (keep === 'ask') {
    return { bids: bids.filter((level) => parseFloat(level.Price) < bestAsk), asks };
  }
  return { bids, asks: asks.filter((level) => parseFloat(level.Price) > bestBid) };
}

/** Reserved line-tool id for the live liquidity ladder. Never persist it. */
export const MARKET_DEPTH_TOOL_TYPE = 'MarketDepth';
export const MARKET_DEPTH_OVERLAY_ID = 'liquidity-market-depth';

export function isMarketDepthDrawing(drawing) {
  return drawing?.toolType === MARKET_DEPTH_TOOL_TYPE
    || drawing?.id === MARKET_DEPTH_OVERLAY_ID;
}

export function withoutMarketDepthDrawings(drawings) {
  if (!Array.isArray(drawings)) return [];
  return drawings.filter((drawing) => !isMarketDepthDrawing(drawing));
}

/**
 * Nearest `count` book rows to the inside market.
 * Bids: highest prices. Asks: lowest prices.
 * Shape matches the market-depth plugin (`Price` + `TotalSize` strings).
 */
export function nearestBookSide(levels, count, side) {
  const n = Math.max(1, Math.min(50, parseInt(count, 10) || 5));
  const rows = [];
  for (const level of Array.isArray(levels) ? levels : []) {
    const price = parseFloat(level?.Price);
    const size = parseFloat(level?.TotalSize ?? level?.Size);
    if (!Number.isFinite(price) || !Number.isFinite(size) || size <= 0) continue;
    rows.push({
      Price: String(level.Price),
      TotalSize: String(size),
      price,
    });
  }
  rows.sort((a, b) => (side === 'bid' ? b.price - a.price : a.price - b.price));
  return rows.slice(0, n).map(({ Price, TotalSize }) => ({ Price, TotalSize }));
}

/** Min/max price of the levels we intend to draw, with a little padding. */
export function priceSpanForLevels(priceValues) {
  let min = Infinity;
  let max = -Infinity;
  for (const value of Array.isArray(priceValues) ? priceValues : []) {
    const price = typeof value === 'number' ? value : parseFloat(value?.Price ?? value);
    if (!Number.isFinite(price)) continue;
    if (price < min) min = price;
    if (price > max) max = price;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  const span = max - min;
  const pad = span > 0 ? span * 0.06 : Math.max(Math.abs(max) * 0.002, 0.01);
  return { min: min - pad, max: max + pad };
}

/**
 * Drop size labels that would sit on top of each other.
 * Larger sizes keep their label when two prices are closer than `minGap` pixels.
 */
export function selectReadableSizeLabels(labels, minGap) {
  const gap = Math.max(1, Number(minGap) || 1);
  const sorted = [...(Array.isArray(labels) ? labels : [])].sort((a, b) => {
    if (b.size !== a.size) return b.size - a.size;
    return a.y - b.y;
  });
  const placed = [];
  for (const label of sorted) {
    if (!Number.isFinite(label?.y)) continue;
    if (placed.some((kept) => Math.abs(kept.y - label.y) < gap)) continue;
    placed.push(label);
  }
  return placed;
}
